import { AxiosResponse } from "axios";
import { del, get, post, put, routes } from "api";
import { IClientConfig, ResponseFormat } from "api/types";
import { IHAQWithContent } from "hooks/api/GQL/haq/useHAQsWithContent.models";
import {
  IAssetContent,
  IAssetRelationshipRequestData,
  IBulkAssetsActionRequestData,
  ICreateAssetContentRequestData,
  ICreateAssetGenericPayload,
  ICreateAssetVersion,
  ICreateUploadTokenResponseData,
  IDeleteAssetsRequestData,
  IGetAssetRelationshipParams,
  ILockBulkAssetResponseData,
  IUpdateStructuredDocumentContentRequestData,
  TCreateUploadTokenRequestData,
} from "models/assets.models";
import { IDocument } from "models/documents.models";
import { IHAQ, IHAQPayload } from "models/HAQ.models";
import { AssetStatus } from "utils/constants/assets.constants";

export type TAssetStatus = {
  status: string;
};

const assets = {
  getAsset(id: string, config?: IClientConfig): Promise<AxiosResponse> {
    return get(routes.asset.assets.byId(id), config);
  },

  updateAssets(data: IDocument[]) {
    return put(routes.asset.assets.all(), { data });
  },

  getAssetStatuses(assetId: string): Promise<AxiosResponse<TAssetStatus[]>> {
    return get(routes.asset.assets.updateAssetStatus(assetId), {
      params: { condensed: true },
    });
  },

  updateAssetStatus(assetId: string, assetStatus: AssetStatus) {
    return post(routes.asset.assets.updateAssetStatus(assetId), {
      data: {
        assetId: assetId,
        status: assetStatus,
      },
    });
  },

  updateAsset(data: IHAQ | IHAQWithContent | IDocument, assetId: string) {
    return put(routes.asset.assets.byId(assetId), { data });
  },

  getDataDictionary(key: string): Promise<AxiosResponse> {
    return get(routes.asset.assets.dataDictionary(key));
  },

  createAssets(
    data: IHAQPayload | ICreateAssetGenericPayload,
    isMockApiOn?: boolean,
  ) {
    return post(routes.asset.assets.all(), { data, isMockApiOn });
  },

  deleteAsset(assetId: string): Promise<AxiosResponse> {
    return del(routes.asset.assets.byId(assetId));
  },

  deleteAssets(data: IDeleteAssetsRequestData): Promise<AxiosResponse> {
    return post(routes.asset.assets.deleteAssets(), { data });
  },

  getAllAssetVersion(
    id: string,
    isMockApiOn?: boolean,
  ): Promise<AxiosResponse> {
    return get(routes.asset.assets.allVersions(id), { isMockApiOn });
  },

  createAssetVersion(
    id: string,
    contentId: string,
    data: ICreateAssetVersion,
    isMockApiOn?: boolean,
  ) {
    return post(routes.asset.assets.createVersion(id, contentId), {
      data,
      isMockApiOn,
    });
  },

  deleteAssetVersion(assetId: string, version: string) {
    return del(routes.asset.assets.deleteVersion(assetId, version));
  },

  getAssetShareHistory(
    id: string,
    isMockApiOn?: boolean,
  ): Promise<AxiosResponse> {
    return get(routes.asset.assets.getAssetShareHistory(id), { isMockApiOn });
  },

  getAssetBulkShareHistory(ids: string[]): Promise<AxiosResponse> {
    return get(routes.asset.assets.getAssetBulkShareHistory(ids));
  },

  getAssetContent(
    assetId: string,
    config?: IClientConfig,
  ): Promise<AxiosResponse> {
    return get(routes.asset.assets.content(assetId), config);
  },

  getContentById(
    assetId: string,
    contentId: string,
  ): Promise<AxiosResponse<IAssetContent>> {
    return get(routes.asset.assets.getContentById(assetId, contentId));
  },

  getBulkContentByIds(
    assetIds: string[],
    format: ResponseFormat = ResponseFormat.Default,
  ): Promise<AxiosResponse<IAssetContent[]>> {
    return get(routes.asset.assets.getBulkContentByIds(), {
      params: {
        assetIds: assetIds.join(","),
        format,
      },
    });
  },

  createAssetContent: (
    assetId: string,
    data: ICreateAssetContentRequestData,
  ): Promise<AxiosResponse> => {
    return post<IAssetContent>(routes.asset.assets.content(assetId), {
      data,
    });
  },

  getCommentThreads: (assetId: string): Promise<AxiosResponse> => {
    return get(routes.asset.assets.commentThreads(assetId));
  },

  createAssetRelationship: (
    assetId: string,
    data: IAssetRelationshipRequestData,
  ) => {
    return post(routes.asset.assets.relationships(assetId), {
      data,
    });
  },

  getAssetRelationships: (
    assetId: string,
    params: IGetAssetRelationshipParams,
  ): Promise<AxiosResponse> => {
    return get(routes.asset.assets.relationships(assetId), {
      params,
    });
  },

  deleteAssetRelationship: (
    assetId: string,
    relationshipId: string,
  ): Promise<AxiosResponse> => {
    return del(routes.asset.assets.deleteRelationship(assetId, relationshipId));
  },

  bulkAssetsAction: (data: IBulkAssetsActionRequestData) => {
    return post<ILockBulkAssetResponseData[]>(
      routes.asset.assets.bulkAssetsAction(),
      {
        data,
      },
    );
  },

  createUploadToken: (
    data: TCreateUploadTokenRequestData,
  ): Promise<AxiosResponse<ICreateUploadTokenResponseData>> => {
    return post(routes.asset.assets.createUploadToken(), { data });
  },

  updateStructuredDocumentContent: (
    assetId: string,
    data: IUpdateStructuredDocumentContentRequestData,
  ): Promise<AxiosResponse> => {
    return put<IAssetContent>(
      routes.asset.assets.structuredDocumentContent(assetId),
      {
        data,
      },
    );
  },

  getStructuredDocumentContent: (
    assetId: string,
    config?: IClientConfig,
  ): Promise<AxiosResponse<string>> => {
    return get(routes.asset.assets.structuredDocumentContent(assetId), config);
  },
};

export default assets;
